/**
 * Coordinate Transformation Utilities
 *
 * Handles conversions between:
 * - Data units (microvolts μV, seconds)
 * - Screen pixels
 * - Canvas coordinates
 *
 * Supports independent zoom levels for X (time) and Y (amplitude) axes
 */

export interface ViewportConfig {
  /** Width of canvas in pixels */
  width: number;
  /** Height of canvas in pixels */
  height: number;
  /** Number of channels to display */
  numChannels: number;
  /** Padding around edges in pixels */
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
}

export interface TimeScaleConfig {
  /** Start time in seconds */
  startTime: number;
  /** End time in seconds */
  endTime: number;
  /** Zoom factor for horizontal axis (1 = normal, 2 = 2x zoom, etc.) */
  zoomX: number;
}

export interface AmplitudeScaleConfig {
  /** Sensitivity in μV/mm (microvolts per millimeter) */
  sensitivity: number;
  /** Zoom factor for vertical axis (1 = normal, 2 = 2x zoom, etc.) */
  zoomY: number;
  /** Pixels per millimeter (typically 3.78 for 96 DPI displays) */
  pixelsPerMm: number;
  /** Enable auto-scaling to fit data within channel bounds */
  autoScale?: boolean;
}

let cachedScreenDpiEnvironmentKey: string | null = null;
let cachedScreenDpi: number | null = null;

function getScreenDpiEnvironmentKey(): string | null {
  if (typeof window === "undefined") return null;
  return [
    Number.isFinite(window.devicePixelRatio) ? window.devicePixelRatio : 1,
    Number.isFinite(window.visualViewport?.scale)
      ? window.visualViewport?.scale
      : 1,
    Number.isFinite(window.innerWidth) ? window.innerWidth : 0,
    Number.isFinite(window.innerHeight) ? window.innerHeight : 0,
    Number.isFinite(window.screen?.width) ? window.screen.width : 0,
    Number.isFinite(window.screen?.height) ? window.screen.height : 0,
  ].join(":");
}

/**
 * Per-channel statistics for auto-scaling
 */
export interface ChannelStatistics {
  min: number;
  max: number;
  mean: number;
  range: number;
}

export interface ChannelConfig {
  /** Index of the channel (0-based) */
  channelIndex: number;
  /** Total number of channels */
  totalChannels: number;
}

/**
 * CoordinateTransform class handles all coordinate system conversions
 */
export class CoordinateTransform {
  private viewport: ViewportConfig;
  private timeScale: TimeScaleConfig;
  private amplitudeScale: AmplitudeScaleConfig;
  private channelStats: Map<number, ChannelStatistics> = new Map();

  constructor(
    viewport: ViewportConfig,
    timeScale: TimeScaleConfig,
    amplitudeScale: AmplitudeScaleConfig,
  ) {
    this.viewport = viewport;
    this.timeScale = timeScale;
    this.amplitudeScale = amplitudeScale;
  }

  /**
   * Calculate and store statistics for a channel's data
   * @param channelIndex - Index of the channel
   * @param data - Channel data array
   */
  setChannelStatistics(channelIndex: number, data: ArrayLike<number>): void {
    if (data.length === 0) {
      this.channelStats.set(channelIndex, {
        min: 0,
        max: 0,
        mean: 0,
        range: 0,
      });
      return;
    }

    let min = Infinity;
    let max = -Infinity;
    let sum = 0;

    for (let i = 0; i < data.length; i++) {
      const val = data[i];
      if (val < min) min = val;
      if (val > max) max = val;
      sum += val;
    }

    const mean = sum / data.length;
    const range = max - min;

    this.channelStats.set(channelIndex, { min, max, mean, range });
  }

  /**
   * Get statistics for a channel
   */
  getChannelStatistics(channelIndex: number): ChannelStatistics | undefined {
    return this.channelStats.get(channelIndex);
  }

  /**
   * Clear all channel statistics (call when loading new data)
   */
  clearChannelStatistics(): void {
    this.channelStats.clear();
  }

  // ========== Time (X-axis) Conversions ==========

  /**
   * Convert time in seconds to canvas X coordinate (pixels)
   */
  timeToX(time: number): number {
    const { startTime, endTime, zoomX } = this.timeScale;
    const { width, padding } = this.viewport;

    const plotWidth = width - padding.left - padding.right;
    const duration = endTime - startTime;
    const normalizedTime = (time - startTime) / duration;

    return padding.left + normalizedTime * plotWidth * zoomX;
  }

  /**
   * Convert canvas X coordinate (pixels) to time in seconds
   */
  xToTime(x: number): number {
    const { startTime, endTime, zoomX } = this.timeScale;
    const { width, padding } = this.viewport;

    const plotWidth = width - padding.left - padding.right;
    const duration = endTime - startTime;
    const normalizedX = (x - padding.left) / (plotWidth * zoomX);

    return startTime + normalizedX * duration;
  }

  /**
   * Get the visible time range based on current zoom
   */
  getVisibleTimeRange(): { start: number; end: number } {
    const { startTime, endTime, zoomX } = this.timeScale;
    const duration = endTime - startTime;
    const visibleDuration = duration / zoomX;

    return {
      start: startTime,
      end: startTime + visibleDuration,
    };
  }

  // ========== Amplitude (Y-axis) Conversions ==========

  /**
   * Convert amplitude in μV to vertical offset in pixels for a specific channel
   * When autoScale is enabled, uses per-channel statistics to normalize data
   */
  amplitudeToY(amplitude: number, channelConfig: ChannelConfig): number {
    const { sensitivity, zoomY, pixelsPerMm, autoScale } = this.amplitudeScale;
    const { height, padding } = this.viewport;

    // Calculate channel baseline Y position
    const plotHeight = height - padding.top - padding.bottom;
    const channelHeight = plotHeight / channelConfig.totalChannels;
    const baselineY =
      padding.top + (channelConfig.channelIndex + 0.5) * channelHeight;

    // Use auto-scaling if enabled and channel statistics are available
    if (autoScale) {
      const stats = this.channelStats.get(channelConfig.channelIndex);
      if (stats && stats.range > 0) {
        // Normalize amplitude to fit within 80% of channel height (10% margin top/bottom)
        const usableHeight = channelHeight * 0.8;
        const normalizedAmplitude = (amplitude - stats.mean) / stats.range;
        const offsetY = normalizedAmplitude * usableHeight * zoomY;
        return baselineY - offsetY;
      }
    }

    // Fallback to traditional μV/mm scaling
    const pixelsPerUv = pixelsPerMm / sensitivity;
    const offsetY = amplitude * pixelsPerUv * zoomY;

    // Invert Y axis (positive values go up)
    return baselineY - offsetY;
  }

  /**
   * Convert canvas Y coordinate to amplitude in μV for a specific channel
   */
  yToAmplitude(y: number, channelConfig: ChannelConfig): number {
    const { sensitivity, zoomY, pixelsPerMm } = this.amplitudeScale;
    const { height, padding } = this.viewport;

    // Calculate channel baseline Y position
    const plotHeight = height - padding.top - padding.bottom;
    const channelHeight = plotHeight / channelConfig.totalChannels;
    const baselineY =
      padding.top + (channelConfig.channelIndex + 0.5) * channelHeight;

    // Calculate offset from baseline (inverted)
    const offsetY = baselineY - y;

    // Convert pixels to amplitude: pixels → mm → μV
    const pixelsPerUv = pixelsPerMm / sensitivity;
    return offsetY / (pixelsPerUv * zoomY);
  }

  /**
   * Get the baseline Y coordinate for a channel (center line)
   */
  getChannelBaselineY(channelConfig: ChannelConfig): number {
    const { height, padding } = this.viewport;
    const plotHeight = height - padding.top - padding.bottom;
    const channelHeight = plotHeight / channelConfig.totalChannels;

    return padding.top + (channelConfig.channelIndex + 0.5) * channelHeight;
  }

  /**
   * Get the top and bottom boundaries for a channel
   */
  getChannelBounds(channelConfig: ChannelConfig): {
    top: number;
    bottom: number;
  } {
    const { height, padding } = this.viewport;
    const plotHeight = height - padding.top - padding.bottom;
    const channelHeight = plotHeight / channelConfig.totalChannels;

    return {
      top: padding.top + channelConfig.channelIndex * channelHeight,
      bottom: padding.top + (channelConfig.channelIndex + 1) * channelHeight,
    };
  }

  // ========== Utility Methods ==========

  /**
   * Determine which channel the Y coordinate is in
   */
  getChannelAtY(y: number): number | null {
    const { height, padding, numChannels } = this.viewport;

    if (y < padding.top || y > height - padding.bottom) {
      return null;
    }

    const plotHeight = height - padding.top - padding.bottom;
    const channelHeight = plotHeight / numChannels;
    const channelIndex = Math.floor((y - padding.top) / channelHeight);

    return Math.max(0, Math.min(numChannels - 1, channelIndex));
  }

  /**
   * Check if a point is within the plot area
   */
  isInPlotArea(x: number, y: number): boolean {
    const { width, height, padding } = this.viewport;

    return (
      x >= padding.left &&
      x <= width - padding.right &&
      y >= padding.top &&
      y <= height - padding.bottom
    );
  }

  /**
   * Get the plot area dimensions
   */
  getPlotArea(): { x: number; y: number; width: number; height: number } {
    const { width, height, padding } = this.viewport;

    return {
      x: padding.left,
      y: padding.top,
      width: width - padding.left - padding.right,
      height: height - padding.top - padding.bottom,
    };
  }

  /**
   * Calculate grid line positions for time axis
   * @param majorInterval - Major grid interval in seconds
   * @param minorInterval - Minor grid interval in seconds (optional)
   */
  getTimeGridLines(
    majorInterval: number,
    minorInterval?: number,
  ): {
    major: Array<{ time: number; x: number }>;
    minor: Array<{ time: number; x: number }>;
  } {
    const { start, end } = this.getVisibleTimeRange();
    const major: Array<{ time: number; x: number }> = [];
    const minor: Array<{ time: number; x: number }> = [];

    // Major grid lines
    const startMajor = Math.ceil(start / majorInterval) * majorInterval;
    for (let time = startMajor; time <= end; time += majorInterval) {
      major.push({ time, x: this.timeToX(time) });
    }

    // Minor grid lines (if specified)
    if (minorInterval) {
      const startMinor = Math.ceil(start / minorInterval) * minorInterval;
      for (let time = startMinor; time <= end; time += minorInterval) {
        // Skip if coincides with major grid line
        if (Math.abs(time % majorInterval) > minorInterval / 2) {
          minor.push({ time, x: this.timeToX(time) });
        }
      }
    }

    return { major, minor };
  }

  /**
   * Calculate grid line positions for amplitude axis
   * @param channelConfig - Configuration for the channel
   * @param majorInterval - Major grid interval in μV
   * @param minorInterval - Minor grid interval in μV (optional)
   */
  getAmplitudeGridLines(
    channelConfig: ChannelConfig,
    majorInterval: number,
    minorInterval?: number,
  ): {
    major: Array<{ amplitude: number; y: number }>;
    minor: Array<{ amplitude: number; y: number }>;
  } {
    const { sensitivity, zoomY, pixelsPerMm } = this.amplitudeScale;
    const bounds = this.getChannelBounds(channelConfig);
    const channelHeight = bounds.bottom - bounds.top;

    // Calculate amplitude range visible in this channel
    const maxAmplitude =
      channelHeight / 2 / (pixelsPerMm / sensitivity) / zoomY;

    const major: Array<{ amplitude: number; y: number }> = [];
    const minor: Array<{ amplitude: number; y: number }> = [];

    // Major grid lines
    const maxMajorLines = Math.floor(maxAmplitude / majorInterval);
    for (let i = -maxMajorLines; i <= maxMajorLines; i++) {
      const amplitude = i * majorInterval;
      major.push({ amplitude, y: this.amplitudeToY(amplitude, channelConfig) });
    }

    // Minor grid lines (if specified)
    if (minorInterval) {
      const maxMinorLines = Math.floor(maxAmplitude / minorInterval);
      for (let i = -maxMinorLines; i <= maxMinorLines; i++) {
        const amplitude = i * minorInterval;
        // Skip if coincides with major grid line
        if (Math.abs(amplitude % majorInterval) > minorInterval / 2) {
          minor.push({
            amplitude,
            y: this.amplitudeToY(amplitude, channelConfig),
          });
        }
      }
    }

    return { major, minor };
  }

  /**
   * Update viewport configuration
   */
  updateViewport(viewport: Partial<ViewportConfig>): void {
    this.viewport = { ...this.viewport, ...viewport };
  }

  /**
   * Update time scale configuration
   */
  updateTimeScale(timeScale: Partial<TimeScaleConfig>): void {
    this.timeScale = { ...this.timeScale, ...timeScale };
  }

  /**
   * Update amplitude scale configuration
   */
  updateAmplitudeScale(amplitudeScale: Partial<AmplitudeScaleConfig>): void {
    this.amplitudeScale = { ...this.amplitudeScale, ...amplitudeScale };
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return {
      viewport: { ...this.viewport },
      timeScale: { ...this.timeScale },
      amplitudeScale: { ...this.amplitudeScale },
    };
  }
}

/**
 * Helper function to calculate pixels per millimeter based on DPI
 * Standard displays: 96 DPI → 3.78 px/mm
 * Retina displays: 192 DPI → 7.56 px/mm
 */
export function calculatePixelsPerMm(dpi: number = 96): number {
  return dpi / 25.4; // 25.4 mm per inch
}

/**
 * Helper function to detect screen DPI
 */
export function detectScreenDPI(): number {
  const environmentKey = getScreenDpiEnvironmentKey();
  if (
    environmentKey &&
    cachedScreenDpiEnvironmentKey === environmentKey &&
    cachedScreenDpi !== null
  ) {
    return cachedScreenDpi;
  }

  if (typeof document === "undefined" || !document.body) {
    return 96;
  }

  // Create a test element
  const testElement = document.createElement("div");
  testElement.style.width = "1in";
  testElement.style.visibility = "hidden";
  testElement.style.position = "absolute";
  document.body.appendChild(testElement);

  const dpi = testElement.offsetWidth;
  document.body.removeChild(testElement);

  const resolvedDpi = dpi || 96;
  if (environmentKey) {
    cachedScreenDpiEnvironmentKey = environmentKey;
    cachedScreenDpi = resolvedDpi;
  }

  return resolvedDpi; // Default to 96 if detection fails
}
